import type {
  Airport,
  FareDetailsV2,
  TripDetailsV2,
} from "@b2bportal/air-shopping-api";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import { Airline, FareRestrictions } from "@tenant/flights";
import clsx from "clsx";
import dayjs from "dayjs";
import defaultStyles from "./ReviewFare.module.scss";

export interface ReviewFareComponentProps extends PlatformComponentProps {
  tripDetails: TripDetailsV2;
  sliceIndex: number;
  fareDetails: FareDetailsV2;
  airports: { [key: string]: Airport };
}

export const ReviewFare = ({
  tripDetails,
  sliceIndex,
  fareDetails,
  airports,
  className,
  children,
}: ReviewFareComponentProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.ReviewFare;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { formatDateTime, DateTimeFormatStyle } = useI18nContext();

  const tripSlice = tripDetails.slices[sliceIndex];

  return (
    <div className={clsx(block, className)}>
      <div className={cn("header")}>
        <div className={cn("title")}>
          <Trans
            i18nKey={"flightReview.itineraryTitle"}
            values={{
              fromCity: airports[tripSlice.originCode].cityName,
              toCity: airports[tripSlice.destinationCode].cityName,
              date: formatDateTime(
                dayjs(removeTimezone(tripSlice.departureTime)).toDate(),
                DateTimeFormatStyle.ShortMonthDayShortWeekday
              ),
            }}
            components={{ 1: <span className={cn("highlight")} /> }}
          />
        </div>
        <Airline
          code={tripSlice.segmentDetails[0].marketingAirline.code}
          name={tripSlice.segmentDetails[0].marketingAirline.name}
        />
      </div>
      <FareRestrictions
        fareSlice={fareDetails.slices[sliceIndex]}
        showAllFareDetails={true}
      />
      {children}
    </div>
  );
};
